import React, { useState, useMemo } from 'react';
import {
  Modal, Button, Form, Table, Badge, Alert,
  InputGroup, Spinner, ButtonGroup
} from 'react-bootstrap';
import {
  Search, TrendingUp, TrendingDown, Calculator,
  CheckCircle, XCircle, Package
} from 'lucide-react';
import { stockService } from '../../services/stockService';

const MOVEMENT_TYPES = [
  { key: 'ENTREE',     label: 'Entrée',     icon: TrendingUp,   variant: 'success', hint: 'Quantité à AJOUTER au stock' },
  { key: 'SORTIE',     label: 'Sortie',     icon: TrendingDown, variant: 'danger',  hint: 'Quantité à RETIRER du stock' },
  { key: 'AJUSTEMENT', label: 'Ajustement', icon: Calculator,   variant: 'warning', hint: 'Nouvelle quantité TOTALE après comptage' }
];

const BulkStockModal = ({ show, onHide, stockItems = [], onSuccess }) => {

  const [type, setType] = useState('ENTREE');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [quantities, setQuantities] = useState({}); // { productId: valeur }
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const activeType = MOVEMENT_TYPES.find((t) => t.key === type);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return stockItems;
    const q = search.toLowerCase();
    return stockItems.filter(
      (i) =>
        i.productName?.toLowerCase().includes(q) ||
        i.productSku?.toLowerCase().includes(q) ||
        i.categoryName?.toLowerCase().includes(q)
    );
  }, [stockItems, search]);

  // Lignes réellement remplies
  const filledLines = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, v]) => v !== '' && v !== null && !isNaN(parseInt(v, 10)))
        .map(([productId, v]) => ({ productId, quantity: parseInt(v, 10) })),
    [quantities]
  );

  const handleQuantityChange = (productId, value) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
  };

  const resetAll = () => {
    setQuantities({});
    setReason('');
    setSearch('');
    setResults(null);
  };

  const handleClose = () => {
    resetAll();
    setType('ENTREE');
    onHide();
  };

  // Aperçu du stock résultant pour une ligne
  const previewNewStock = (item) => {
    const raw = quantities[item.productId];
    if (raw === '' || raw === undefined || isNaN(parseInt(raw, 10))) return null;

    const qty = parseInt(raw, 10);
    const current = item.quantity || 0;

    if (type === 'ENTREE') return current + qty;
    if (type === 'SORTIE') return current - qty;
    return qty; // AJUSTEMENT
  };

  const handleSubmit = async () => {
    if (filledLines.length === 0) return;

    try {
      setSubmitting(true);
      const response = await stockService.createBulkStockMovement(type, filledLines, reason);

      if (response.success && response.data) {
        setResults(response.data);
        if (response.data.successCount > 0) {
          onSuccess?.(response.data);
        }
      } else {
        setResults({
          totalRequested: filledLines.length,
          successCount: 0,
          failureCount: filledLines.length,
          results: [{ productName: 'Requête', success: false, message: response.message }]
        });
      }
    } catch (err) {
      console.error('❌ Erreur soumission groupée:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Écran de résultats ----------
  if (results) {
    return (
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Résultat du mouvement groupé</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-3 mb-3">
            <Badge bg="success" className="px-3 py-2">
              <CheckCircle size={14} className="me-1" />
              {results.successCount} réussie(s)
            </Badge>
            {results.failureCount > 0 && (
              <Badge bg="danger" className="px-3 py-2">
                <XCircle size={14} className="me-1" />
                {results.failureCount} en échec
              </Badge>
            )}
          </div>

          <Table size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Produit</th>
                <th className="text-center">Avant</th>
                <th className="text-center">Après</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {results.results?.map((r, idx) => (
                <tr key={idx} className={r.success ? '' : 'table-danger'}>
                  <td>{r.productName}</td>
                  <td className="text-center">{r.oldQuantity ?? '—'}</td>
                  <td className="text-center fw-semibold">{r.newQuantity ?? '—'}</td>
                  <td>
                    <small className={r.success ? 'text-success' : 'text-danger'}>
                      {r.message}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {results.failureCount > 0 && (
            <Alert variant="warning" className="mt-3 mb-0 small">
              Les lignes réussies ont bien été enregistrées. Corrigez les lignes en
              échec et relancez un mouvement pour celles-ci uniquement.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={resetAll}>
            Nouveau mouvement groupé
          </Button>
          <Button variant="primary" onClick={handleClose}>
            Terminer
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // ---------- Écran de saisie ----------
  return (
    <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <Package size={22} className="me-2" />
          Gestion groupée du stock
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Type de mouvement */}
        <div className="mb-3">
          <Form.Label className="small fw-semibold">Type de mouvement</Form.Label>
          <div>
            <ButtonGroup>
              {MOVEMENT_TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <Button
                    key={t.key}
                    variant={type === t.key ? t.variant : `outline-${t.variant}`}
                    onClick={() => setType(t.key)}
                    disabled={submitting}
                  >
                    <Icon size={16} className="me-1" />
                    {t.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          </div>
          <Form.Text className="text-muted">{activeType?.hint}</Form.Text>
        </div>

        {/* Motif */}
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Motif (appliqué à toutes les lignes)</Form.Label>
          <Form.Control
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex : Livraison fournisseur du 08/09, Inventaire trimestriel..."
            disabled={submitting}
          />
        </Form.Group>

        {/* Recherche */}
        <InputGroup className="mb-2">
          <InputGroup.Text><Search size={16} /></InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Filtrer les produits..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={submitting}
          />
        </InputGroup>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted">
            {filteredItems.length} produit(s) affiché(s)
          </small>
          {filledLines.length > 0 && (
            <Badge bg="primary">{filledLines.length} ligne(s) à valider</Badge>
          )}
        </div>

        {/* Tableau de saisie */}
        <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
          <Table size="sm" hover className="mb-0">
            <thead className="sticky-top bg-white">
              <tr>
                <th>Produit</th>
                <th className="text-center" style={{ width: 90 }}>Stock</th>
                <th className="text-center" style={{ width: 130 }}>
                  {type === 'AJUSTEMENT' ? 'Compté' : 'Quantité'}
                </th>
                <th className="text-center" style={{ width: 90 }}>Après</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const newStock = previewNewStock(item);
                const invalid = newStock !== null && newStock < 0;

                return (
                  <tr key={item.productId} className={invalid ? 'table-danger' : ''}>
                    <td>
                      <div className="fw-semibold small">{item.productName}</div>
                      <small className="text-muted">
                        {item.productSku || '—'} • {item.categoryName}
                      </small>
                    </td>
                    <td className="text-center align-middle">
                      <Badge bg={item.stockLevelColor || 'secondary'}>
                        {item.quantity ?? 0}
                      </Badge>
                    </td>
                    <td className="align-middle">
                      <Form.Control
                        type="number"
                        size="sm"
                        min={0}
                        value={quantities[item.productId] ?? ''}
                        onChange={(e) => handleQuantityChange(item.productId, e.target.value)}
                        placeholder="—"
                        disabled={submitting}
                        className="text-center"
                      />
                    </td>
                    <td className="text-center align-middle">
                      {newStock !== null ? (
                        <span className={invalid ? 'text-danger fw-bold' : 'fw-semibold'}>
                          {newStock}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {filledLines.some((l) => {
          const item = stockItems.find((i) => i.productId === l.productId);
          return type === 'SORTIE' && item && (item.quantity || 0) < l.quantity;
        }) && (
          <Alert variant="warning" className="mt-3 mb-0 small">
            Certaines sorties dépassent le stock disponible. Ces lignes seront
            rejetées individuellement — les autres passeront quand même.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="me-auto small text-muted">
          Laissez une case vide pour ignorer le produit.
        </div>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Annuler
        </Button>
        <Button
          variant={activeType?.variant || 'primary'}
          onClick={handleSubmit}
          disabled={filledLines.length === 0 || submitting}
        >
          {submitting ? (
            <><Spinner size="sm" className="me-2" />Traitement...</>
          ) : (
            <>Valider {filledLines.length > 0 ? `(${filledLines.length})` : ''}</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkStockModal;